import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  SCALING_VIEW_OPTIONS,
  OVERTIME_OPTIONS,
  OVERTIME_BOUNDS,
  BREAKDOWN_BOUNDS,
  PALETTE_OPTIONS,
  SECTION_CONFIG,
  getSectionOptions,
  getBreakdownOptions,
} from "../../benchmarkAdminConfig";
import {
  BENCHMARK_METRIC_GROUPS,
  getPaletteColors,
} from "../../benchmarkMetricConfig";
import BenchmarkVersionHistory from "../BenchmarkVersionHistory/BenchmarkVersionHistory";
import styles from "./AdminBenchmarkPanel.module.scss";

/** Radio group where exactly one option is selected. */
const RadioRow = ({ name, options, value, onChange }) => (
  <div className={styles.optionList}>
    {options.map((opt) => (
      <label key={opt.value} className={styles.optionRow}>
        <input
          type="radio"
          name={name}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
        />
        <span>{opt.label}</span>
      </label>
    ))}
  </div>
);

/**
 * Checkbox group that enforces a min/max number of selections: unchecking is
 * blocked at `min`, checking is blocked at `max`.
 */
const CheckboxGroup = ({ options, values, min, max, onChange }) => {
  const selected = values || [];
  const atMax = max != null && selected.length >= max;
  const atMin = min != null && selected.length <= min;

  const toggle = (val) => {
    const isOn = selected.includes(val);
    if (isOn) {
      if (atMin) return;
      onChange(selected.filter((v) => v !== val));
    } else {
      if (atMax) return;
      onChange([...selected, val]);
    }
  };

  return (
    <div className={styles.optionList}>
      {options.map((opt) => {
        const checked = selected.includes(opt.value);
        const disabled = (!checked && atMax) || (checked && atMin);
        return (
          <label
            key={opt.value}
            className={`${styles.optionRow} ${disabled ? styles.optionRowDisabled : ""}`}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={() => toggle(opt.value)}
            />
            <span>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
};

const Section = ({ title, hint, children }) => (
  <div className={styles.section}>
    <div className={styles.sectionHead}>
      <span className={styles.sectionTitle}>{title}</span>
      {hint ? <span className={styles.sectionHint}>{hint}</span> : null}
    </div>
    {children}
  </div>
);

const AdminBenchmarkPanel = ({
  config,
  onChange,
  entities = [],
  panelStyle,
  canSave = false,
  currentVersion = null,
  currentLabel = null,
  saving = false,
  onSave,
  // Version tab
  onOpenVersions,
  versions = [],
  versionsTotal = 0,
  versionsPage = 0,
  versionsLimit = 20,
  versionsLoading = false,
  previewVersion = null,
  reverting = false,
  onLoadVersions,
  onPreviewVersion,
  onRevertVersion,
}) => {
  const [activeTab, setActiveTab] = useState("customize");

  // Guests never have the Version tab; keep them on Customize.
  useEffect(() => {
    if (!canSave && activeTab !== "customize") setActiveTab("customize");
  }, [canSave, activeTab]);

  const goToVersions = () => {
    setActiveTab("version");
    onOpenVersions?.();
  };
  // Options are mode-independent so the customization is identical (and applies
  // consistently) across every benchmark mode — institution / researcher /
  // unit / venue. Metrics a given mode's data doesn't return are simply not
  // rendered in that mode's charts, but the saved selection never changes.
  const impactOptions = useMemo(
    () => getSectionOptions(BENCHMARK_METRIC_GROUPS.IMPACT),
    []
  );
  const capacityOptions = useMemo(
    () => getSectionOptions(BENCHMARK_METRIC_GROUPS.CAPACITY),
    []
  );
  const momentumOptions = useMemo(
    () => getSectionOptions(BENCHMARK_METRIC_GROUPS.MOMENTUM),
    []
  );
  const breakdownOptions = useMemo(() => getBreakdownOptions(), []);

  const set = (field, value) => onChange({ ...config, [field]: value });

  const setEntityColor = (id, hex) =>
    onChange({
      ...config,
      entityColors: { ...(config.entityColors || {}), [id]: hex },
    });

  const clearEntityColor = (id) => {
    const next = { ...(config.entityColors || {}) };
    delete next[id];
    onChange({ ...config, entityColors: next });
  };

  const resetColors = () =>
    onChange({ ...config, palette: "default", entityColors: {} });

  if (typeof document === "undefined" || !document.body) return null;

  return createPortal(
    <aside
      className={styles.panel}
      style={panelStyle}
      aria-label="Benchmark customization"
    >
      <div className={styles.header}>
        <div>
          <div className={styles.headerTitle}>Benchmark view</div>
          <div className={styles.headerSubtitle}>
            {currentVersion != null
              ? `Version ${currentVersion}${currentLabel ? ` · ${currentLabel}` : ""}`
              : canSave
                ? "Unsaved configuration"
                : "Default configuration"}
          </div>
        </div>
      </div>

      {canSave ? (
        <div className={styles.tabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "customize"}
            className={`${styles.tab} ${
              activeTab === "customize" ? styles["tab--active"] : ""
            }`}
            onClick={() => setActiveTab("customize")}
          >
            Customize
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "version"}
            className={`${styles.tab} ${
              activeTab === "version" ? styles["tab--active"] : ""
            }`}
            onClick={goToVersions}
          >
            Version
          </button>
        </div>
      ) : (
        <div className={styles.guestNote}>
          Log in to save this configuration and access version history.
        </div>
      )}

      <div className={styles.body}>
        {activeTab === "version" && canSave ? (
          <BenchmarkVersionHistory
            versions={versions}
            total={versionsTotal}
            page={versionsPage}
            limit={versionsLimit}
            loading={versionsLoading}
            currentVersion={currentVersion}
            previewVersion={previewVersion}
            reverting={reverting}
            onPageChange={onLoadVersions}
            onPreview={onPreviewVersion}
            onRevert={onRevertVersion}
          />
        ) : (
        <>
          {canSave ? (
            <button
              type="button"
              className={styles.saveBtnFull}
              onClick={onSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save version"}
            </button>
          ) : null}
        <Section title="Metric scaling view" hint="choose 1">
          <RadioRow
            name="scalingView"
            options={SCALING_VIEW_OPTIONS}
            value={config.scalingView}
            onChange={(v) => set("scalingView", v)}
          />
        </Section>

        <Section
          title="Citation Impact"
          hint={`${config.citationImpact.length}/4 · choose 1–4`}
        >
          <CheckboxGroup
            options={impactOptions}
            values={config.citationImpact}
            min={SECTION_CONFIG[BENCHMARK_METRIC_GROUPS.IMPACT].min}
            max={SECTION_CONFIG[BENCHMARK_METRIC_GROUPS.IMPACT].max}
            onChange={(v) => set("citationImpact", v)}
          />
        </Section>

        <Section
          title="Capacity & Productivity"
          hint={`${config.capacity.length}/2 · choose 0–2`}
        >
          <CheckboxGroup
            options={capacityOptions}
            values={config.capacity}
            min={SECTION_CONFIG[BENCHMARK_METRIC_GROUPS.CAPACITY].min}
            max={SECTION_CONFIG[BENCHMARK_METRIC_GROUPS.CAPACITY].max}
            onChange={(v) => set("capacity", v)}
          />
        </Section>

        <Section
          title="Momentum & Reach"
          hint={`${config.momentum.length}/2 · choose 0–2`}
        >
          <CheckboxGroup
            options={momentumOptions}
            values={config.momentum}
            min={SECTION_CONFIG[BENCHMARK_METRIC_GROUPS.MOMENTUM].min}
            max={SECTION_CONFIG[BENCHMARK_METRIC_GROUPS.MOMENTUM].max}
            onChange={(v) => set("momentum", v)}
          />
        </Section>

        <Section title="Display heatmap">
          <RadioRow
            name="showHeatmap"
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
            value={config.showHeatmap ? "yes" : "no"}
            onChange={(v) => set("showHeatmap", v === "yes")}
          />
        </Section>

        <Section title="Display related concepts">
          <RadioRow
            name="showRelatedConcepts"
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
            value={config.showRelatedConcepts ? "yes" : "no"}
            onChange={(v) => set("showRelatedConcepts", v === "yes")}
          />
        </Section>

        <Section
          title="Over-time line charts"
          hint={`${config.overTime.length}/3 · choose up to 3`}
        >
          <CheckboxGroup
            options={OVERTIME_OPTIONS}
            values={config.overTime}
            min={OVERTIME_BOUNDS.min}
            max={OVERTIME_BOUNDS.max}
            onChange={(v) => set("overTime", v)}
          />
        </Section>

        <Section
          title="Research Topics Specific Breakdown"
          hint={`${config.breakdown.length}/6 · choose 1–6`}
        >
          <CheckboxGroup
            options={breakdownOptions}
            values={config.breakdown}
            min={BREAKDOWN_BOUNDS.min}
            max={BREAKDOWN_BOUNDS.max}
            onChange={(v) => set("breakdown", v)}
          />
        </Section>

        <Section title="Chart colors" hint="palette & per-entity">
          <div className={styles.paletteList}>
            {PALETTE_OPTIONS.map((opt) => {
              const swatches = getPaletteColors(opt.value).slice(0, 6);
              return (
                <label key={opt.value} className={styles.paletteRow}>
                  <input
                    type="radio"
                    name="palette"
                    checked={config.palette === opt.value}
                    onChange={() => set("palette", opt.value)}
                  />
                  <span className={styles.paletteLabel}>{opt.label}</span>
                  <span className={styles.paletteSwatches}>
                    {swatches.map((c, i) => (
                      <span
                        key={i}
                        className={styles.paletteSwatch}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </span>
                </label>
              );
            })}
          </div>

          {entities.length > 0 ? (
            <div className={styles.entityColorList}>
              {entities.map((ent) => {
                const overridden = Boolean(config.entityColors?.[ent.id]);
                return (
                  <div key={ent.id} className={styles.entityColorRow}>
                    <input
                      type="color"
                      className={styles.colorInput}
                      value={ent.color}
                      onChange={(e) => setEntityColor(ent.id, e.target.value)}
                      aria-label={`Color for ${ent.name}`}
                    />
                    <span className={styles.entityColorName}>{ent.name}</span>
                    {overridden ? (
                      <button
                        type="button"
                        className={styles.entityColorReset}
                        onClick={() => clearEntityColor(ent.id)}
                        aria-label={`Reset color for ${ent.name}`}
                        title="Reset to palette"
                      >
                        ↺
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.colorsHint}>
              Add entities to set individual colors.
            </div>
          )}

          <button
            type="button"
            className={styles.resetColorsBtn}
            onClick={resetColors}
          >
            Reset colors
          </button>
        </Section>
        </>
        )}
      </div>
    </aside>,
    document.body
  );
};

export default AdminBenchmarkPanel;
